import { useMemo } from 'react'

import {
  matchFilterArrayIncludes, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { EntityOptions } from '@redux/modules/types'

const watchEntityKeys = ['orderBundles']

interface OrderBundleFilters {
  agency_id?: number,
  global?: string,
  hidden?: string,
  max_price?: number,
  min_price?: number,
  provider_id?: number,
  provider_price_list_id?: number,
  publication_id?: number,
  service_product_id?: number,
  string?: string,
}

type UseEntitiesOptions = {
  entityKey?: string,
  filters?: OrderBundleFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

function useOrderBundles(options: UseEntitiesOptions = {}) {
  const { filters, requestOptions } = options

  const {
    global: filterGlobal,
    hidden: filterHidden,
    max_price: filterMaxPrice,
    min_price: filterMinPrice,
    provider_id: filterProviderId,
    provider_price_list_id: filterProviderPriceList,
    publication_id: filterPublicationId,
    service_product_id: filterServiceProductId,
    string,
  } = filters || {}

  const {
    updatedEntities: { orderBundles: orderBundlesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { orderBundles } = useSelector(reduxState => reduxState.entities)

  const filteredOrderBundles = useMemo(() => {
    const filtered = Object.values(orderBundles).filter((orderBundle) => {
      const {
        assigned_price_list_ids = [],
        assigned_provider_ids = [],
        assigned_publication_ids = [],
        assigned_service_product_ids = [],
        global,
        hidden,
        id,
        price,
        title,
      } = orderBundle

      const matchGlobal = filterGlobal ? global?.toString() === filterGlobal : true
      const matchHidden = filterHidden ? hidden?.toString() === filterHidden : true
      const matchMaxPrice = filterMaxPrice ? price <= Number(filterMaxPrice) : true
      const matchMinPrice = filterMinPrice ? price >= Number(filterMinPrice) : true
      const matchPriceList = matchFilterArrayIncludes(assigned_price_list_ids, Number(filterProviderPriceList))
      const matchProvider = matchFilterArrayIncludes(assigned_provider_ids, Number(filterProviderId))
      const matchPublicationId = matchFilterArrayIncludes(assigned_publication_ids, Number(filterPublicationId))
      const matchServiceProductId = matchFilterArrayIncludes(assigned_service_product_ids, filterServiceProductId)

      // String match (ID & Title)
      const matchId = id.toString() === string
      const matchTitle = matchFilterString(title, string)
      const matchString = matchId || matchTitle

      return matchGlobal
        && matchHidden
        && matchMaxPrice
        && matchMinPrice
        && matchPriceList
        && matchProvider
        && matchPublicationId
        && matchServiceProductId
        && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [orderBundlesUpdatedAt, JSON.stringify(filters)])

  const filteredOrderBundlesCount = filteredOrderBundles.length
  const hasFilteredOrderBundles = !!filteredOrderBundles.length

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredOrderBundlesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingOrderBundles } = useReduxAction(
    'orderBundles',
    'loadOrderBundles',
    {
      ...filtersWithOffset,
      ...requestOptions,
      limit,
    },
    [performHttpRequests, filtersWithOffset],
    // @ts-expect-error TS2339
    { shouldPerformFn: ({ loading }) => performHttpRequests && !loading },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredOrderBundles,
    filteredOrderBundlesCount,
    hasFilteredOrderBundles,
    loading: loadingOrderBundles,
  }
}

export default useOrderBundles
